import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Settings, Users, Target, DollarSign, Calendar, Percent } from 'lucide-react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { salesService } from '../services/salesService';
import { Origem, Indicador } from '../types/sales';

const CommercialSettings = () => {
  const [activeTab, setActiveTab] = useState<'origens' | 'indicadores'>('origens');
  const [origens, setOrigens] = useState<Origem[]>([]);
  const [indicadores, setIndicadores] = useState<Indicador[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Estados para modais
  const [showOrigemModal, setShowOrigemModal] = useState(false);
  const [showIndicadorModal, setShowIndicadorModal] = useState(false);
  const [editingOrigem, setEditingOrigem] = useState<Origem | null>(null);
  const [editingIndicador, setEditingIndicador] = useState<Indicador | null>(null);

  // Estados para formulários
  const [origemForm, setOrigemForm] = useState({ nome: '' });
  const [indicadorForm, setIndicadorForm] = useState({ 
    nome: '', 
    telefone: '', 
    email: '',
    tem_recorrencia: false,
    porcentagem_recorrencia: 0,
    valor_total_recorrencia: 0,
    data_pagamento_recorrencia: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [origensData, indicadoresData] = await Promise.all([
        salesService.getOrigens(),
        salesService.getIndicadores()
      ]);
      setOrigens(origensData);
      setIndicadores(indicadoresData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrigem = async () => {
    try {
      await salesService.createOrigem(origemForm);
      setOrigemForm({ nome: '' });
      setShowOrigemModal(false);
      loadData();
      toast({
        title: "Sucesso",
        description: "Origem criada com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao criar origem:', error);
      toast({
        title: "Erro",
        description: "Erro ao criar origem. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const handleCreateIndicador = async () => {
    try {
      await salesService.createIndicador(indicadorForm);
      setIndicadorForm({ 
        nome: '', 
        telefone: '', 
        email: '',
        tem_recorrencia: false,
        porcentagem_recorrencia: 0,
        valor_total_recorrencia: 0,
        data_pagamento_recorrencia: ''
      });
      setShowIndicadorModal(false);
      loadData();
      toast({
        title: "Sucesso",
        description: "Indicador criado com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao criar indicador:', error);
      toast({
        title: "Erro",
        description: "Erro ao criar indicador. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const handleEditOrigem = (origem: Origem) => {
    setEditingOrigem(origem);
    setOrigemForm({ nome: origem.nome });
    setShowOrigemModal(true);
  };

  const handleEditIndicador = (indicador: Indicador) => {
    setEditingIndicador(indicador);
    setIndicadorForm({ 
      nome: indicador.nome, 
      telefone: indicador.telefone || '', 
      email: indicador.email || '',
      tem_recorrencia: indicador.tem_recorrencia,
      porcentagem_recorrencia: indicador.porcentagem_recorrencia || 0,
      valor_total_recorrencia: indicador.valor_total_recorrencia || 0,
      data_pagamento_recorrencia: indicador.data_pagamento_recorrencia || ''
    });
    setShowIndicadorModal(true);
  };

  const handleUpdateOrigem = async () => {
    if (!editingOrigem) return;
    try {
      await salesService.updateOrigem(editingOrigem.id, origemForm);
      setEditingOrigem(null);
      setOrigemForm({ nome: '' });
      setShowOrigemModal(false);
      loadData();
      toast({
        title: "Sucesso",
        description: "Origem atualizada com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao atualizar origem:', error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar origem. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateIndicador = async () => {
    if (!editingIndicador) return;
    try {
      await salesService.updateIndicador(editingIndicador.id, indicadorForm);
      setEditingIndicador(null);
      setIndicadorForm({ 
        nome: '', 
        telefone: '', 
        email: '',
        tem_recorrencia: false,
        porcentagem_recorrencia: 0,
        valor_total_recorrencia: 0,
        data_pagamento_recorrencia: ''
      });
      setShowIndicadorModal(false);
      loadData();
      toast({
        title: "Sucesso",
        description: "Indicador atualizado com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao atualizar indicador:', error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar indicador. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteOrigem = async (id: number) => {
    if (!confirm('Tem certeza que deseja excluir esta origem?')) return;
    try {
      await salesService.deleteOrigem(id);
      loadData();
      toast({
        title: "Sucesso",
        description: "Origem excluída com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao excluir origem:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir origem. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteIndicador = async (id: number) => {
    if (!confirm('Tem certeza que deseja excluir este indicador?')) return;
    try {
      await salesService.deleteIndicador(id);
      loadData();
      toast({
        title: "Sucesso",
        description: "Indicador excluído com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao excluir indicador:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir indicador. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const filteredOrigens = origens.filter(origem => 
    origem.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredIndicadores = indicadores.filter(indicador => 
    indicador.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    indicador.telefone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    indicador.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="text-4xl font-bold text-slate-900 flex items-center gap-3">
              <Settings className="h-10 w-10 text-blue-600" />
              Configurações Comerciais
            </h1>
            <p className="text-lg text-slate-600">
              Gerencie origens de leads e indicadores para o sistema de CRM
            </p>
          </div>
          
          {/* Stats Cards */}
          <div className="flex gap-4">
            <Card className="w-48">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Target className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Origens</p>
                    <p className="text-2xl font-bold text-slate-900">{origens.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="w-48">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Users className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Indicadores</p>
                    <p className="text-2xl font-bold text-slate-900">{indicadores.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Main Content */}
        <Card className="shadow-xl border-0">
          <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl text-slate-900">Gestão de Dados Comerciais</CardTitle>
                <CardDescription className="text-slate-600 mt-1">
                  Configure as origens de leads e gerencie indicadores com sistema de recorrência
                </CardDescription>
              </div>
              
              {/* Search and Add Button */}
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-64 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'origens' | 'indicadores')}>
              <TabsList className="w-full justify-start rounded-none border-b bg-slate-50">
                <TabsTrigger 
                  value="origens" 
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
                >
                  <Target className="h-4 w-4 mr-2" />
                  Origens de Leads ({origens.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="indicadores"
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Indicadores ({indicadores.length})
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="origens" className="p-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-slate-900">Origens de Leads</h3>
                    <Button
                      onClick={() => {
                        setEditingOrigem(null);
                        setOrigemForm({ nome: '' });
                        setShowOrigemModal(true);
                      }}
                      className="bg-blue-600 hover:bg-blue-700 shadow-lg"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Nova Origem
                    </Button>
                  </div>
                  
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {filteredOrigens.length === 0 ? (
                        <Card className="border-dashed border-2 border-slate-200">
                          <CardContent className="p-12 text-center">
                            <Target className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-slate-900 mb-2">Nenhuma origem cadastrada</h3>
                            <p className="text-slate-600 mb-4">Comece criando sua primeira origem de leads</p>
                            <Button
                              onClick={() => {
                                setEditingOrigem(null);
                                setOrigemForm({ nome: '' });
                                setShowOrigemModal(true);
                              }}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Criar Primeira Origem
                            </Button>
                          </CardContent>
                        </Card>
                      ) : (
                        filteredOrigens.map((origem) => (
                          <Card key={origem.id} className="hover:shadow-md transition-shadow">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className="p-2 bg-blue-100 rounded-lg">
                                    <Target className="h-5 w-5 text-blue-600" />
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-slate-900">{origem.nome}</h4>
                                    <p className="text-sm text-slate-600">
                                      Criado em {new Date(origem.created_at).toLocaleDateString('pt-BR')}
                                    </p>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditOrigem(origem)}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteOrigem(origem.id)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="indicadores" className="p-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-slate-900">Indicadores</h3>
                    <Button
                      onClick={() => {
                        setEditingIndicador(null);
                        setIndicadorForm({ 
                          nome: '', 
                          telefone: '', 
                          email: '',
                          tem_recorrencia: false,
                          porcentagem_recorrencia: 0,
                          valor_total_recorrencia: 0,
                          data_pagamento_recorrencia: ''
                        });
                        setShowIndicadorModal(true);
                      }}
                      className="bg-green-600 hover:bg-green-700 shadow-lg"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Novo Indicador
                    </Button>
                  </div>
                  
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {filteredIndicadores.length === 0 ? (
                        <Card className="border-dashed border-2 border-slate-200">
                          <CardContent className="p-12 text-center">
                            <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-slate-900 mb-2">Nenhum indicador cadastrado</h3>
                            <p className="text-slate-600 mb-4">Comece criando seu primeiro indicador</p>
                            <Button
                              onClick={() => {
                                setEditingIndicador(null);
                                setIndicadorForm({ 
                                  nome: '', 
                                  telefone: '', 
                                  email: '',
                                  tem_recorrencia: false,
                                  porcentagem_recorrencia: 0,
                                  valor_total_recorrencia: 0,
                                  data_pagamento_recorrencia: ''
                                });
                                setShowIndicadorModal(true);
                              }}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Criar Primeiro Indicador
                            </Button>
                          </CardContent>
                        </Card>
                      ) : (
                        filteredIndicadores.map((indicador) => (
                          <Card key={indicador.id} className="hover:shadow-md transition-shadow">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className="p-2 bg-green-100 rounded-lg">
                                    <Users className="h-5 w-5 text-green-600" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-1">
                                      <h4 className="font-semibold text-slate-900">{indicador.nome}</h4>
                                      {indicador.tem_recorrencia && (
                                        <Badge variant="secondary" className="bg-green-100 text-green-700">
                                          <DollarSign className="h-3 w-3 mr-1" />
                                          Recorrência
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 text-sm text-slate-600">
                                      <div>
                                        <p><strong>Telefone:</strong> {indicador.telefone}</p>
                                        {indicador.email && (
                                          <p><strong>Email:</strong> {indicador.email}</p>
                                        )}
                                      </div>
                                      {indicador.tem_recorrencia && (
                                        <div>
                                          <p><strong>Porcentagem:</strong> {indicador.porcentagem_recorrencia}%</p>
                                          <p><strong>Total Recebido:</strong> R$ {Number(indicador.valor_total_recorrencia || 0).toFixed(2)}</p>
                                        </div>
                                      )}
                                    </div>
                                    <p className="text-xs text-slate-500 mt-2">
                                      Criado em {new Date(indicador.created_at).toLocaleDateString('pt-BR')}
                                    </p>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditIndicador(indicador)}
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteIndicador(indicador.id)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Origem Modal */}
        <Dialog open={showOrigemModal} onOpenChange={setShowOrigemModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-blue-600" />
                {editingOrigem ? 'Editar Origem' : 'Nova Origem'}
              </DialogTitle>
              <DialogDescription>
                {editingOrigem ? 'Edite os dados da origem' : 'Cadastre uma nova origem de leads'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="origem-nome">Nome da Origem *</Label>
                <Input
                  id="origem-nome"
                  value={origemForm.nome}
                  onChange={(e) => setOrigemForm({ nome: e.target.value })}
                  placeholder="Ex: Google Ads, LinkedIn, Indicação..."
                  required
                  className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <Button 
                variant="outline" 
                onClick={() => setShowOrigemModal(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={editingOrigem ? handleUpdateOrigem : handleCreateOrigem}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {editingOrigem ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Indicador Modal */}
        <Dialog open={showIndicadorModal} onOpenChange={setShowIndicadorModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-green-600" />
                {editingIndicador ? 'Editar Indicador' : 'Novo Indicador'}
              </DialogTitle>
              <DialogDescription>
                {editingIndicador ? 'Edite os dados do indicador' : 'Cadastre um novo indicador de leads'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              {/* Informações Básicas */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Informações Básicas</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="indicador-nome">Nome *</Label>
                    <Input
                      id="indicador-nome"
                      value={indicadorForm.nome}
                      onChange={(e) => setIndicadorForm({ ...indicadorForm, nome: e.target.value })}
                      placeholder="Nome do indicador"
                      required
                      className="border-slate-200 focus:border-green-500 focus:ring-green-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="indicador-telefone">Telefone *</Label>
                    <Input
                      id="indicador-telefone"
                      type="tel"
                      value={indicadorForm.telefone}
                      onChange={(e) => setIndicadorForm({ ...indicadorForm, telefone: e.target.value })}
                      placeholder="(00) 00000-0000"
                      required
                      className="border-slate-200 focus:border-green-500 focus:ring-green-500"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="indicador-email">Email</Label>
                  <Input
                    id="indicador-email"
                    type="email"
                    value={indicadorForm.email}
                    onChange={(e) => setIndicadorForm({ ...indicadorForm, email: e.target.value })}
                    placeholder="email@exemplo.com"
                    className="border-slate-200 focus:border-green-500 focus:ring-green-500"
                  />
                </div>
              </div>

              {/* Sistema de Recorrência */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    Sistema de Recorrência
                  </h3>
                  <Switch
                    checked={indicadorForm.tem_recorrencia}
                    onCheckedChange={(checked) => setIndicadorForm({ ...indicadorForm, tem_recorrencia: checked })}
                  />
                </div>
                
                {indicadorForm.tem_recorrencia && (
                  <div className="space-y-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="indicador-porcentagem" className="flex items-center gap-2">
                          <Percent className="h-4 w-4" />
                          Porcentagem de Recorrência (%)
                        </Label>
                        <Input
                          id="indicador-porcentagem"
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={indicadorForm.porcentagem_recorrencia}
                          onChange={(e) => setIndicadorForm({ ...indicadorForm, porcentagem_recorrencia: parseFloat(e.target.value) || 0 })}
                          placeholder="0.00"
                          className="border-green-200 focus:border-green-500 focus:ring-green-500"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="indicador-valor-total" className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4" />
                          Valor Total Recebido (R$)
                        </Label>
                        <Input
                          id="indicador-valor-total"
                          type="number"
                          min="0"
                          step="0.01"
                          value={indicadorForm.valor_total_recorrencia}
                          onChange={(e) => setIndicadorForm({ ...indicadorForm, valor_total_recorrencia: parseFloat(e.target.value) || 0 })}
                          placeholder="0.00"
                          className="border-green-200 focus:border-green-500 focus:ring-green-500"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="indicador-data-pagamento" className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Data do Último Pagamento
                      </Label>
                      <Input
                        id="indicador-data-pagamento"
                        type="date"
                        value={indicadorForm.data_pagamento_recorrencia}
                        onChange={(e) => setIndicadorForm({ ...indicadorForm, data_pagamento_recorrencia: e.target.value })}
                        className="border-green-200 focus:border-green-500 focus:ring-green-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <Button 
                variant="outline" 
                onClick={() => setShowIndicadorModal(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={editingIndicador ? handleUpdateIndicador : handleCreateIndicador}
                className="bg-green-600 hover:bg-green-700"
              >
                {editingIndicador ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CommercialSettings;